import { useState } from 'react';
import {
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useGetEmployeeMessages from './hooks/useGetEmployeeMessages';
import dayjs from 'dayjs';
import LoadingOverlay from 'components/shared/LoadingOverlay';
import { useTranslation } from 'react-i18next';
import {
  MessageForm,
  TaskDefinitionGroup,
  TaskDefinitionStatus
} from './types';
import { schema } from './schema';
import ActionsButtons from './components/ActionButtons';
import Filters from './components/Filters';
import { RED, RED_LIGHT, rgbaPrimary, WHITE } from 'constants/colors';
import { Icon } from '@sdb/planning-ui';

type MessagesProps = {
  employeeId: string;
  enableActions?: boolean;
  isEmployeeCard?: boolean;
};

const Messages = (props: MessagesProps) => {
  const PAGE_SIZE = 15;

  const { employeeId, enableActions, isEmployeeCard } = props;
  const { t } = useTranslation('common');

  const [uncollapsedMessages, setUncollapsedMessages] = useState<number[]>([]);
  const [pageNr, setPageNr] = useState<number>(0); // Index
  const [selectedMessages, setSelectedMessages] = useState<string[]>([]); // Track selected messages

  const { control, watch } = useForm<MessageForm>({
    defaultValues: schema.cast({
      type: {
        label: t('employeeMessages.type.ALL'),
        value: TaskDefinitionGroup.ALL
      },
      status: {
        label: t('employeeMessages.status.OPEN'),
        value: TaskDefinitionStatus.OPEN
      }
    }),
    resolver: yupResolver(schema)
  });

  const type: TaskDefinitionGroup = watch('type').value;
  const status: TaskDefinitionStatus = watch('status').value;

  const range = `items=${pageNr * PAGE_SIZE}-${
    pageNr * PAGE_SIZE - 1 + PAGE_SIZE
  }`;

  const {
    data,
    isLoading,
    refetch: refetchMessages
  } = useGetEmployeeMessages(employeeId, status, type, range, isEmployeeCard);
  const [messages, [, , rangeTotal]] = data || [[], [0, 0, 0]];

  const messagesWithSeenAction =
    messages.filter(({ actions }) => actions?.[0]?.seen) || [];

  const handleSelectAll = () => {
    const allMessageIds = messagesWithSeenAction.map(({ taskId }) => taskId);
    setSelectedMessages(allMessageIds);
  };

  const handleDeselectAll = () => {
    setSelectedMessages([]);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      minHeight="calc(100vh - 200px)"
      width="calc(100% - 36px)"
      gap={2}
      mt={2}
      ml={2}
    >
      <Filters
        control={control}
        onPageChange={(pageNr: number) => setPageNr(pageNr - 1)} // Endpoint are 0-indexed
        pageCount={Math.ceil(rangeTotal / PAGE_SIZE)}
        refetchMessages={refetchMessages}
        selectedMessages={selectedMessages}
        allMessagesSelected={
          selectedMessages.length === messagesWithSeenAction.length
        }
        enableBulkActions={messagesWithSeenAction.length > 0}
        onSelectAll={handleSelectAll}
        onDeselectAll={handleDeselectAll}
        isEmployeeCard={isEmployeeCard}
      />

      {messages.length === 0 && !isLoading && (
        <Typography variant="h6" align="center" mt={6}>
          {t('employeeMessages.noMessages')}
        </Typography>
      )}

      {messages.map(
        (
          { actions, message, createTime, taskDefinitionKey, taskId },
          i: number
        ) => {
          const isUncollapsed = uncollapsedMessages?.includes(i);
          const createdAtDate = dayjs(createTime);
          const isRejectedDuty =
            taskDefinitionKey ===
              'AgendaDutyLineCreateNotifyEmployeeRejected' ||
            taskDefinitionKey === 'AgendaDutyLineEditNotifyEmployeeRejected';
          const showCheckbox = actions && actions[0]?.seen;
          const selectedMessage = selectedMessages.includes(taskId);

          return (
            <Paper
              key={i}
              sx={{
                bgcolor: isRejectedDuty
                  ? RED_LIGHT
                  : selectedMessage
                  ? rgbaPrimary(0.05)
                  : WHITE
              }}
            >
              <List>
                <Box
                  display="flex"
                  alignItems="center"
                  pl={showCheckbox ? 1 : 0}
                >
                  {showCheckbox && !isEmployeeCard && (
                    <Checkbox
                      name={taskId}
                      checked={selectedMessage}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedMessages([...selectedMessages, taskId]);
                        } else {
                          setSelectedMessages(
                            selectedMessages.filter((id) => id !== taskId)
                          );
                        }
                      }}
                    />
                  )}
                  <ListItem
                    onClick={setUncollapsedMessages.bind(
                      this,
                      isUncollapsed
                        ? uncollapsedMessages?.filter((m) => m !== i)
                        : [...uncollapsedMessages, i]
                    )}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      pl: showCheckbox ? 1 : 3,
                      backgroundColor: isUncollapsed
                        ? 'rgba(0, 0, 0, 0.05)'
                        : 'transparent'
                    }}
                  >
                    <Box
                      dangerouslySetInnerHTML={{ __html: message?.subject }}
                      sx={{
                        '& h3': {
                          color: isRejectedDuty ? RED : 'inherit'
                        }
                      }}
                    />
                    <Box display="flex" alignItems="center">
                      {enableActions && (
                        <ActionsButtons
                          taskId={taskId}
                          actions={actions}
                          isRejectedDuty={isRejectedDuty}
                          sx={{ mr: 1 }}
                        />
                      )}

                      <ListItemText
                        primary={
                          createdAtDate.isValid()
                            ? createdAtDate.format('DD/MM/YYYY · HH:mm')
                            : ''
                        }
                        sx={{ mr: 2 }}
                      />

                      <Icon
                        type={isUncollapsed ? 'chevron-up' : 'chevron-down'}
                        fontSize="inherit"
                      />
                    </Box>
                  </ListItem>
                </Box>

                {/* Content */}
                <ListItem
                  sx={{
                    overflow: 'hidden',
                    maxHeight: isUncollapsed ? '100vh' : '0px',
                    padding: isUncollapsed ? '16px' : '0px',
                    opacity: isUncollapsed ? 1 : 0,
                    transition: 'max-height 150ms ease-out'
                  }}
                >
                  <Box dangerouslySetInnerHTML={{ __html: message?.body }} />
                </ListItem>
              </List>
            </Paper>
          );
        }
      )}
      <LoadingOverlay loading={isLoading} />
    </Box>
  );
};

export default Messages;
